import { useState } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import clsx from "clsx";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import Divider from "../../../../components/divider";
import DividerWithText from "../../../../components/dividerWithText";
import PhotoCaptureBox from "../../../../components/photoCaptureBox";
import { useMutation } from "@tanstack/react-query";
import { parsePartNumber } from "../../../../api/orderService";
import ParseConfirmModal from "../../../../components/modals/ParseConfirmModal";
import ParseFailedModal from "../../../../components/modals/ParseFailedModal";
import type { OrderFormValues } from "../../../../types/forms";
import { makeOptions } from "../../../../constants";
import { useSearchParams } from "react-router-dom";
import { ANALYTICS_EVENTS, trackEvent } from "../../../../lib/analytics";

type ParsedDataType = {
  partNumber: string;
  vehicleMake?: string;
};

export default function PartInformation() {
  const {
    register,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<OrderFormValues>();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("id") || "";

  const [hasCapturedPhoto, setHasCapturedPhoto] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedDataType | null>(null);
  const [parseConfirmModal, setParseConfirmModal] = useState(false);
  const [parseFailedModal, setParseFailedModal] = useState(false);
  const [parseFailureReason, setParseFailureReason] = useState<
    "missing_order" | "invalid_result" | "api_error" | null
  >(null);

  const trackingNumber = useWatch({
    control,
    name: "trackingInfo.trackingNumber",
  });
  const isReadOnly = !!trackingNumber;

  const [prevFormState, setPrevFormState] = useState<{
    partNumber: string;
    vehicleMake: string;
  } | null>(null);

  const [manualEntryTracked, setManualEntryTracked] = useState(false);

  const partNumberRegister = register("orderLineItems.0.partInfo.partNumber", {
    required: "Part number is required",
    minLength: {
      value: 1,
      message: "Part number must be at least 1 character",
    },
    maxLength: {
      value: 50,
      message: "Part number must be at most 50 characters",
    },
  });

  const handleManualEntryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!manualEntryTracked && e.target.value) {
      trackEvent(ANALYTICS_EVENTS.PART_MANUAL_ENTRY);
      setManualEntryTracked(true);
    }
  };

  const { mutate: parseImage, isPending: isParsing } = useMutation({
    mutationFn: ({
      file,
      serviceType,
    }: {
      file: File;
      serviceType: string;
    }) => {
      if (!orderId) {
        return Promise.reject(new Error("Missing orderId"));
      }
      return parsePartNumber(file, serviceType, orderId);
    },
    retry: false,
    networkMode: "always",
    onSuccess: (data) => {
      const result = data?.partNumber;
      const isValidString = typeof result === "string" && result.length > 0;
      const isTooLong = isValidString && result.length > 20;

      if (isValidString && !isTooLong) {
        setParsedData({
          partNumber: result,
          vehicleMake: data?.vehicleMake,
        });
        setParseConfirmModal(true);
        trackEvent(ANALYTICS_EVENTS.PART_PARSE_SUCCESS);
      } else {
        setParseFailureReason("invalid_result");
        setParseFailedModal(true);
        trackEvent(ANALYTICS_EVENTS.PART_PARSE_FAILED, {
          reason: "invalid_result",
        });
      }
    },
    onError: (err) => {
      const reason =
        err instanceof Error && err.message === "Missing orderId"
          ? "missing_order"
          : "api_error";
      setParseFailureReason(reason);
      setParseFailedModal(true);
      trackEvent(ANALYTICS_EVENTS.PART_PARSE_FAILED, { reason });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setValue("partImageFile", file);
    setHasCapturedPhoto(true);

    setPrevFormState({
      partNumber: getValues("orderLineItems.0.partInfo.partNumber"),
      vehicleMake: getValues("orderLineItems.0.partInfo.vehicleMake"),
    });

    trackEvent(ANALYTICS_EVENTS.PART_PHOTO_TAKEN);
    parseImage({
      file,
      serviceType: getValues("orderLineItems.0.serviceType"),
    });
  };

  const resetPhoto = () => {
    setValue("partImageFile", undefined);
    setHasCapturedPhoto(false);
    setParsedData(null);

    const fileInput = document.getElementById(
      "partCameraInput",
    ) as HTMLInputElement;
    if (fileInput) fileInput.value = "";

    if (prevFormState) {
      setValue(
        "orderLineItems.0.partInfo.partNumber",
        prevFormState.partNumber,
      );
      setValue(
        "orderLineItems.0.partInfo.vehicleMake",
        prevFormState.vehicleMake,
      );
    }
  };

  const reopenPartPicker = () => {
    const input = document.getElementById("partCameraInput") as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!input) return;

    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }

    input.click();
  };

  const handleConfirm = () => {
    if (parsedData) {
      setValue("orderLineItems.0.partInfo.partNumber", parsedData.partNumber);

      if (parsedData.vehicleMake) {
        setValue(
          "orderLineItems.0.partInfo.vehicleMake",
          parsedData.vehicleMake,
        );
      }
    }
    setParseConfirmModal(false);
  };

  return (
    <section>
      {isParsing && (
        <div className="fixed inset-0 z-100 bg-white/50 flex items-center justify-center" />
      )}
      <div className="flex flex-col gap-2 mb-3">
        <h2 className="text-lg font-medium text-gray-950">Part Information</h2>
      </div>

      <div className="flex flex-col gap-6">
        <input
          type="file"
          accept="image/*"
          id="partCameraInput"
          onChange={handleFileChange}
          className="absolute h-0 w-0 opacity-0 -z-10"
        />

        <PhotoCaptureBox
          inputId="partCameraInput"
          isParsing={isParsing}
          hasCapturedPhoto={hasCapturedPhoto}
          disabled={isReadOnly}
          idleText="Take a picture of the part"
          parsingText="Analyzing image..."
          onReset={resetPhoto}
        />
      </div>

      <DividerWithText text="Don't have the part in hand? Enter it manually." />

      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="partNumber"
              className="block text-sm/6 font-normal text-gray-950"
            >
              Part Number
            </label>
            <div>
              <input
                id="partNumber"
                type="text"
                autoComplete="off"
                placeholder="Enter part number"
                {...partNumberRegister}
                onChange={(e) => {
                  partNumberRegister.onChange(e);
                  handleManualEntryChange(e);
                }}
                className={clsx(
                  "block w-full rounded-xl border px-4 py-3.5 text-sm outline-none",
                  "focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500",
                  errors.orderLineItems?.[0]?.partInfo?.partNumber
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200",
                )}
              />
              {errors.orderLineItems?.[0]?.partInfo?.partNumber && (
                <p className="mt-2 text-sm text-red-500">
                  {
                    errors.orderLineItems?.[0]?.partInfo?.partNumber
                      ?.message as string
                  }
                </p>
              )}
            </div>
          </div>

          <Controller
            name="orderLineItems.0.partInfo.vehicleMake"
            control={control}
            rules={{ required: "Make is required" }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-2">
                <label className="block text-sm/6 font-normal text-gray-950">
                  Make
                </label>

                <Listbox
                  value={
                    makeOptions.find((o) => o.value === field.value) ?? null
                  }
                  onChange={(opt) => field.onChange(opt?.value)}
                >
                  <ListboxButton
                    className={clsx(
                      "flex w-full items-center justify-between rounded-xl border px-4 py-3.5 text-sm outline-none",
                      "focus:ring-2 focus:ring-blue-500",
                      fieldState.error
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-200",
                    )}
                  >
                    <span className="truncate">
                      {makeOptions.find((o) => o.value === field.value)
                        ?.label ?? (
                        <span className="text-gray-500">Select make</span>
                      )}
                    </span>

                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  </ListboxButton>

                  <ListboxOptions
                    anchor="bottom"
                    className="mt-1 w-(--button-width) rounded-xl border border-gray-200 bg-white p-1 shadow outline-none"
                  >
                    {makeOptions.map((opt) => (
                      <ListboxOption
                        key={opt.value}
                        value={opt}
                        className="cursor-pointer rounded-lg px-3 py-2 text-sm data-focus:bg-blue-50 data-focus:text-blue-500"
                      >
                        {opt.label}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Listbox>

                {fieldState.error && (
                  <p className="text-sm text-red-600">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>
      </div>

      <Divider />

      <ParseConfirmModal
        isOpen={parseConfirmModal}
        parsedData={parsedData?.partNumber || null}
        onClose={() => {
          setParseConfirmModal(false);
          resetPhoto();
        }}
        onConfirm={handleConfirm}
      />

      <ParseFailedModal
        isOpen={parseFailedModal}
        title={
          parseFailureReason === "missing_order"
            ? "Order not found"
            : "Unable to Parse image"
        }
        message={
          parseFailureReason === "missing_order"
            ? "This page was opened without a valid order id. Open the order link from your eBay order confirmation to upload a part photo."
            : parseFailureReason === "api_error"
              ? "We couldn't reach the parsing service. Please check your connection and try again, or enter the part number manually."
              : undefined
        }
        hideTryAgain={parseFailureReason === "missing_order"}
        onClose={() => {
          setParseFailedModal(false);
          setParseFailureReason(null);
          resetPhoto();
        }}
        onTryAgain={() => {
          setParseFailedModal(false);
          setParseFailureReason(null);
          resetPhoto();
          reopenPartPicker();
        }}
      />
    </section>
  );
}
